import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ImagePlus, Trash2, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { imageFilesFromClipboardData } from '@/lib/clipboard/image-clipboard';
import { resizeImageToDataUrl } from '@/lib/images/image-resize';

/**
 * Pick, preview, replace and remove one image. Accepts a click, a drop or a paste, downscales the
 * result in the renderer, and hands back a data URI (or '' when removed) — so the caller can put
 * it straight into a JSON body without ever holding a File.
 *
 * The downscale is not cosmetic: without it a phone photo goes into the request body at full size.
 * The server re-encodes whatever arrives, so `maxDim` here only has to be >= the server's bound.
 *
 * @param {object} props
 * @param {string} props.value        Current image (data URI or http URL); '' when unset.
 * @param {(next: string) => void} props.onChange  Called with a data URI, or '' to remove.
 * @param {'circle'|'portrait'} [props.shape]
 * @param {string} [props.previewClassName]  Sizing for the preview box.
 * @param {number} [props.maxDim]     Long-edge cap in px.
 * @param {React.ReactNode} [props.fallback]  Rendered inside the preview box when there is no image.
 */
export function ImagePicker({
  value,
  onChange,
  shape = 'circle',
  previewClassName,
  maxDim = 1024,
  label,
  hint,
  disabled = false,
  emptyLabel = 'Drop, paste, or click to choose',
}) {
  const inputRef = useRef(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const descriptionId = useId();
  const hasImage = Boolean(String(value || '').trim());
  const isCircle = shape === 'circle';

  const accept = async (file) => {
    if (!file || disabled) return;
    setIsProcessing(true);
    try {
      const { dataUrl } = await resizeImageToDataUrl(file, { maxDim });
      onChange?.(dataUrl);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not read that image.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDraggingOver(false);
    if (disabled) return;
    // Handles drops and pastes alike, and names screenshots that arrive without a filename.
    const [image] = imageFilesFromClipboardData(event.dataTransfer);
    if (!image) {
      toast.error('That does not look like an image.');
      return;
    }
    void accept(image);
  };

  const handlePaste = (event) => {
    if (disabled) return;
    const [image] = imageFilesFromClipboardData(event.clipboardData);
    if (!image) return;
    event.preventDefault();
    void accept(image);
  };

  const openPicker = () => {
    if (disabled || isProcessing) return;
    inputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      {label ? (
        <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      ) : null}

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={label ? `${label}: choose an image` : 'Choose an image'}
        aria-describedby={hint ? descriptionId : undefined}
        aria-disabled={disabled}
        onClick={openPicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker();
          }
        }}
        onPaste={handlePaste}
        onDragOver={(event) => {
          if (disabled) return;
          event.preventDefault();
          setIsDraggingOver(true);
        }}
        onDragLeave={() => setIsDraggingOver(false)}
        onDrop={handleDrop}
        className={cn(
          'group relative flex items-center justify-center overflow-hidden border-2 border-dashed border-border bg-muted/20 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isCircle ? 'rounded-full' : 'rounded-lg',
          hasImage && 'border-solid border-border/60 bg-muted/10',
          isDraggingOver && !disabled && 'border-primary bg-primary/5',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          isProcessing && 'pointer-events-none opacity-70',
          previewClassName,
        )}
      >
        {hasImage ? (
          <img src={value} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex flex-col items-center justify-center gap-1 px-2 text-muted-foreground">
            <ImagePlus className={cn(isCircle ? 'h-5 w-5' : 'h-7 w-7')} aria-hidden />
            {!isCircle ? <span className="text-[11px] leading-snug">{emptyLabel}</span> : null}
          </span>
        )}

        {hasImage && !disabled ? (
          // Icon-only: a text label does not fit inside the 80px circular preview, and truncating
          // it hid the Remove action entirely. Both actions must stay legible at every shape/size.
          <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-background/85 py-1 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <Button
              type="button"
              variant="ghost"
              size="ssm"
              className="h-6 w-6 p-0"
              title="Replace image"
              aria-label="Replace image"
              onClick={(event) => {
                event.stopPropagation();
                openPicker();
              }}
            >
              <Upload className="h-3.5 w-3.5" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="ssm"
              className="h-6 w-6 p-0 text-destructive hover:text-destructive"
              title="Remove image"
              aria-label="Remove image"
              onClick={(event) => {
                event.stopPropagation();
                onChange?.('');
              }}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </span>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Reset so re-picking the same file still fires a change event.
          event.target.value = '';
          void accept(file);
        }}
      />

      {hint ? (
        <p id={descriptionId} className="text-[11px] leading-snug text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
